import { auth } from '../config/firebase';
import { config } from '../config';
import {
  ClientRow,
  RunStamp,
  SupportData,
  TimeRange,
  DashboardData,
  ClientDetailData,
  RepsData,
  RepSeriesData,
  ClientUser,
  ClientUsersData,
  CreateClientUserPayload,
  RepSessionsData,
  SessionDetailData
} from '../types/superadmin';

/** Serialize a time range into the query string the API expects. */
function rangeQuery(range: TimeRange): string {
  if (range.key === 'custom' && range.from && range.to) {
    return `?range=custom&from=${range.from}&to=${range.to}`;
  }
  return `?range=${range.key}`;
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

interface Envelope<T> {
  success: boolean;
  data: T;
  meta?: { lastRun?: RunStamp | null; count?: number; pagination?: Pagination };
  message?: string;
  error?: string;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  /** Message shown when the super-admin allowlist rejects the request. */
  forbiddenMessage?: string;
}

/**
 * Authenticated request against the backend-tenant super-admin API. Attaches
 * the current Firebase ID token; surfaces a friendly message on failure so
 * pages can render an error state instead of crashing.
 */
async function request<T>(path: string, options: RequestOptions = {}): Promise<Envelope<T>> {
  const { method = 'GET', body, forbiddenMessage = 'You do not have access to this data.' } =
    options;
  const user = auth.currentUser;
  if (!user) {
    throw new Error('Your session has expired. Please sign in again.');
  }

  const token = await user.getIdToken();
  let res: Response;
  try {
    res = await fetch(`${config.api.baseURL}/api/superadmin${path}`, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' })
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) })
    });
  } catch (networkError) {
    throw new Error('Could not reach the server. Check your connection and try again.');
  }

  const json = (await res.json().catch(() => ({}))) as Envelope<T>;
  if (!res.ok || json.success === false) {
    // Only the allowlist rejection is a blanket "no access"; every other 403
    // (e.g. trial access required) carries a message worth showing verbatim.
    if (res.status === 403 && json.error === 'FORBIDDEN') {
      throw new Error(forbiddenMessage);
    }
    if (res.status === 404 && json.error === 'NO_SNAPSHOTS') {
      throw new Error('No data yet — the nightly rollup has not run.');
    }
    throw new Error(json.message || `Request failed (${res.status}).`);
  }
  return json;
}

const get = <T>(path: string) => request<T>(path);
const post = <T>(path: string, body: unknown) => request<T>(path, { method: 'POST', body });
const del = <T>(path: string) => request<T>(path, { method: 'DELETE' });
const put = <T>(path: string, body: unknown) =>
  request<T>(path, {
    method: 'PUT',
    body,
    forbiddenMessage: 'You do not have access to change this setting.'
  });

// Per-tenant capability flags surfaced by the Features page (Lushin addition).
export interface TenantFeatureRow {
  tenantId: string;
  displayName: string;
  isActive: boolean;
  features: { liveIntelligence: boolean; training: boolean; livePlaybook: boolean };
}

export const api = {
  getDashboard: (range: TimeRange, includeInternal = false) =>
    get<DashboardData>(
      `/dashboard${rangeQuery(range)}${includeInternal ? '&includeInternal=true' : ''}`
    ),
  getClients: (range: TimeRange) =>
    get<{ range: TimeRange; clients: ClientRow[] }>(`/clients${rangeQuery(range)}`),
  getClient: (tenantId: string, range: TimeRange) =>
    get<ClientDetailData>(`/clients/${encodeURIComponent(tenantId)}${rangeQuery(range)}`),
  getReps: (range: TimeRange) => get<RepsData>(`/reps${rangeQuery(range)}`),
  getRepSeries: (tenantId: string, userId: string, range: TimeRange) =>
    get<RepSeriesData>(
      `/clients/${encodeURIComponent(tenantId)}/reps/${encodeURIComponent(userId)}/series${rangeQuery(range)}`
    ),
  getSupport: () => get<SupportData>('/support'),
  getMeta: () => get<{ lastRun: RunStamp | null }>('/meta'),
  // Live client user management (not snapshot-backed).
  getClientUsers: (tenantId: string) =>
    get<ClientUsersData>(`/clients/${encodeURIComponent(tenantId)}/users`),
  createClientUser: (tenantId: string, payload: CreateClientUserPayload) =>
    post<{ user: ClientUser }>(`/clients/${encodeURIComponent(tenantId)}/users`, payload),
  deactivateClientUser: (tenantId: string, userId: string) =>
    del<{ user: ClientUser }>(
      `/clients/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(userId)}`
    ),

  // Session review — live from the client's database, not the nightly rollup.
  getRepSessions: (tenantId: string, userId: string, range: TimeRange, page = 1) =>
    get<RepSessionsData>(
      `/clients/${encodeURIComponent(tenantId)}/reps/${encodeURIComponent(userId)}/sessions${rangeQuery(range)}&page=${page}`
    ),
  getSession: (tenantId: string, sessionId: string) =>
    get<SessionDetailData>(
      `/clients/${encodeURIComponent(tenantId)}/sessions/${encodeURIComponent(sessionId)}`
    ),

  // Per-tenant capability toggles (Lushin addition).
  getTenants: () =>
    get<{ tenants: TenantFeatureRow[]; count: number }>('/tenants'),
  updateTenantFeatures: (
    tenantId: string,
    features: Partial<{ liveIntelligence: boolean; training: boolean; livePlaybook: boolean }>
  ) =>
    put<{ tenantId: string; displayName: string; features: TenantFeatureRow['features'] }>(
      `/tenants/${encodeURIComponent(tenantId)}/features`,
      { features }
    )
};
